import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Palette,
  Layers,
  Grid3X3,
  Monitor,
  Smartphone,
  ArrowRight,
  Check,
  Sparkles,
} from 'lucide-react';
import { skillsData, personalInfo } from '../data/mock';

const iconMap = {
  Search: Search,
  Palette: Palette,
  Layers: Layers,
  Grid3X3: Grid3X3,
  Monitor: Monitor,
  Smartphone: Smartphone,
};

const ServicesPage = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.reveal-on-scroll').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const processSteps = [
    { step: '01', title: 'Discover', desc: 'Understanding your business, users, and goals through research and workshops.', color: '#00ff88' },
    { step: '02', title: 'Define', desc: 'Synthesizing insights into clear problem statements and design direction.', color: '#7c3aed' },
    { step: '03', title: 'Design', desc: 'Creating wireframes, prototypes, and high-fidelity designs iteratively.', color: '#00ff88' },
    { step: '04', title: 'Deliver', desc: 'Handing off polished designs with documentation and development support.', color: '#7c3aed' },
  ];

  return (
    <div className="relative">
      <div className="gradient-bg" />
      <div className="grid-bg" />
      <div className="noise-overlay" />

      {/* Header */}
      <section className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-4xl">
            <span 
              className="inline-block px-4 py-1.5 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] text-sm font-medium mb-6"
              style={{ animation: 'fadeUp 0.8s 0.1s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              What I Offer
            </span>
            <h1 
              className="text-5xl lg:text-7xl font-bold text-white leading-tight mb-6"
              style={{ animation: 'fadeUp 1s 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(40px)' }}
            >
              Services &{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00ff88] to-[#7c3aed]">Expertise</span>
            </h1>
            <p 
              className="text-white/50 text-xl leading-relaxed"
              style={{ animation: 'fadeUp 1s 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards', opacity: 0, transform: 'translateY(20px)' }}
            >
              From research to final delivery, I offer comprehensive design services that help businesses create products users love.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {skillsData.services.map((service, index) => {
              const IconComponent = iconMap[service.icon] || Sparkles;
              return (
                <div
                  key={service.title}
                  className="reveal-on-scroll stagger-children group glass-card p-8 hover:-translate-y-2 transition-all duration-500 relative overflow-hidden"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  {/* Gradient hover effect */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#00ff88]/0 to-[#7c3aed]/0 group-hover:from-[#00ff88]/5 group-hover:to-[#7c3aed]/5 transition-all duration-500" />
                  
                  <div className="relative z-10">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00ff88]/20 to-[#7c3aed]/20 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-7 h-7 text-[#00ff88]" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-4 group-hover:text-[#00ff88] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-white/50 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#00ff88]/5 via-transparent to-[#7c3aed]/5" />
        
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="reveal-on-scroll stagger-children text-center mb-20">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-4">
              How I Work
            </span>
            <h2 className="text-5xl lg:text-6xl font-bold text-white">
              My Design Process
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((item, index) => (
              <div
                key={item.step}
                className="reveal-on-scroll stagger-children text-center group"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="relative mb-8">
                  <div 
                    className="text-[120px] font-bold leading-none opacity-10 transition-all duration-500 group-hover:opacity-20"
                    style={{ color: item.color }}
                  >
                    {item.step}
                  </div>
                  <div 
                    className="absolute inset-0 flex items-center justify-center"
                  >
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center font-bold text-xl text-black transition-all duration-500 group-hover:scale-110"
                      style={{ backgroundColor: item.color }}
                    >
                      {item.step}
                    </div>
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-[#00ff88] transition-colors">
                  {item.title}
                </h3>
                <p className="text-white/50 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Skills */}
      <section className="py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-20">
            {/* Tools */}
            <div className="reveal-on-scroll stagger-children">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#00ff88]/10 text-[#00ff88] text-sm font-medium mb-4">
                Tool Proficiency
              </span>
              <h2 className="text-4xl font-bold text-white mb-12">Design Tools</h2>

              <div className="space-y-8">
                {skillsData.tools.map((tool, index) => (
                  <div key={tool.name}>
                    <div className="flex justify-between mb-3">
                      <span className="text-white font-medium">{tool.name}</span>
                      <span className="text-[#00ff88] font-bold">{tool.proficiency}%</span>
                    </div>
                    <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-1000 ease-out"
                        style={{ 
                          width: `${tool.proficiency}%`,
                          background: `linear-gradient(90deg, #00ff88 0%, #7c3aed 100%)`
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Soft Skills */}
            <div className="reveal-on-scroll stagger-children">
              <span className="inline-block px-4 py-1.5 rounded-full bg-[#7c3aed]/10 text-[#7c3aed] text-sm font-medium mb-4">
                Beyond Tools
              </span>
              <h2 className="text-4xl font-bold text-white mb-12">Soft Skills</h2>

              <div className="grid grid-cols-2 gap-4">
                {skillsData.softSkills.map((skill, index) => (
                  <div
                    key={skill}
                    className="group glass-card p-5 hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#00ff88]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#00ff88]/30 transition-colors">
                        <Check className="w-4 h-4 text-[#00ff88]" />
                      </div>
                      <span className="text-white font-medium text-sm">{skill}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <div className="reveal-on-scroll stagger-children glass-card p-12 lg:p-16 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-[#00ff88]/20 to-transparent rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-br from-[#7c3aed]/20 to-transparent rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />
            
            <div className="relative z-10">
              <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
                Let's Work Together
              </h2>
              <p className="text-white/50 text-lg mb-10 max-w-xl mx-auto">
                Ready to transform your product with exceptional design? Let's discuss how I can help.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/contact">
                  <button className="group px-10 py-5 bg-[#00ff88] text-black rounded-full font-semibold text-lg hover:shadow-[0_0_40px_rgba(0,255,136,0.4)] transition-all duration-300">
                    <span className="flex items-center gap-2">
                      Start a Project
                      <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  </button>
                </Link>
                <a href={`mailto:${personalInfo.email}`}>
                  <button className="px-10 py-5 bg-white/5 text-white rounded-full font-semibold text-lg border border-white/10 hover:bg-white/10 transition-all">
                    {personalInfo.email}
                  </button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
